/**
 * Identidad en el navegador.
 *
 * Cliente OIDC estándar contra el realm de Keycloak. Se usa la biblioteca
 * genérica y no la específica de Keycloak porque lo que se habla es OpenID
 * Connect: si mañana la identidad se mueve a otro proveedor, cambia la
 * configuración y no el código.
 *
 * El nombre del realm y el del cliente van en minúsculas porque son
 * identificadores técnicos, no menciones de la marca.
 *
 * **Sin secreto, con PKCE.** Esto es JavaScript que se descarga al navegador:
 * cualquier secreto que llevara sería legible abriendo las herramientas de
 * desarrollo. PKCE resuelve el mismo problema —que nadie pueda canjear un
 * código robado— sin necesidad de guardar nada.
 */

import { WebStorageStateStore } from "oidc-client-ts";
import type { AuthProviderProps } from "react-oidc-context";

/**
 * De dónde salen los ajustes.
 *
 * Se hornean en la imagen al construirla, como el resto de `VITE_*`. Eso
 * significa que cambiar de dominio obliga a reconstruir el frontend — es el
 * mismo compromiso que ya tiene LicitAI y conviene tenerlo presente.
 */
// La excepcion solo cubre la linea siguiente, asi que la expresion va entera.
// critero-lint-disable-next-line critero
const AUTORIDAD = import.meta.env.VITE_KEYCLOAK_ISSUER ?? "https://auth.montevive.ai/realms/critero";
// critero-lint-disable-next-line critero
const CLIENTE = import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? "critero-web";

export const authConfig: AuthProviderProps = {
  authority: AUTORIDAD,
  client_id: CLIENTE,
  redirect_uri: `${window.location.origin}/acceso/vuelta`,
  // Al sitio comercial y no a `/`, que ahora es el acceso: cerrar sesion y
  // aterrizar en otra pantalla de login se lee como que no se ha cerrado.
  post_logout_redirect_uri: "https://critero.es",
  response_type: "code",
  scope: "openid profile email",

  // `sessionStorage` y no `localStorage`: la sesión muere al cerrar la pestaña.
  // Es más molesto y es lo correcto — estos tokens dan acceso a diagnósticos de
  // carrera, muchas veces hechos en un ordenador compartido o del trabajo.
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),

  // Renovación silenciosa antes de que caduque el token, que dura 5 minutos.
  // Sin esto, alguien a mitad del cuestionario vería fallar el guardado
  // automático sin entender por qué.
  automaticSilentRenew: true,

  // Limpia el código y el estado de la URL tras volver del proveedor, para que
  // no queden en el historial ni se compartan al copiar la dirección.
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};

/**
 * El token que la API espera en cada petición.
 *
 * Vive fuera de React porque lo necesita el cliente de la API, que son
 * funciones sueltas. Lo escribe el proveedor de autenticación al cambiar la
 * sesión.
 */
let tokenActual: string | null = null;

export function setToken(token: string | null): void {
  tokenActual = token;
}

export function getToken(): string | null {
  return tokenActual;
}

/** Cabecera de autorización, o nada si no hay sesión. */
export function authHeaders(): Record<string, string> {
  return tokenActual ? { Authorization: `Bearer ${tokenActual}` } : {};
}
